import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import axios from '../api/axios';
import { APP_NAME } from '../utils/constants';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import logo from '../assets/logo_transparent.png';

const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

const ForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const { register, handleSubmit, formState: { errors }, getValues } = useForm({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.post('/auth/forgot-password', {
        email: data.email,
      });

      console.log('Password reset email sent:', response.data);
      setSuccess(true);
    } catch (err) {
      console.error('Forgot password error:', err);
      setError(
        err.response?.data?.message || 
        'Unable to process your request. Please try again later.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-500 to-secondary-600 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <Card.Content className="py-8">
            <div className="text-center mb-8">
              <div className="inline-flex mb-4">
                <img src={logo} alt={APP_NAME} className="h-20 w-auto" />
              </div>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Check Your Email</h1>
              <p className="text-gray-600">
                We've sent password reset instructions to
              </p>
              <p className="text-primary-600 font-medium mt-1">
                {getValues('email')}
              </p>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                <p className="font-medium mb-2">📧 What's next?</p>
                <ul className="space-y-1 text-blue-700">
                  <li>• Check your email inbox (and spam folder)</li>
                  <li>• Click the reset link in the email</li>
                  <li>• The link expires in 60 minutes</li>
                </ul>
              </div>

              <div className="text-center text-sm text-gray-600">
                <p>Didn't receive the email?</p>
                <button
                  onClick={() => setSuccess(false)}
                  className="text-primary-600 hover:text-primary-700 font-medium mt-1"
                >
                  Try again
                </button>
              </div>

              <Link
                to="/login"
                className="flex items-center justify-center gap-2 text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Login
              </Link>
            </div>
          </Card.Content>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-500 to-secondary-600 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <Card.Content className="py-8">
          <div className="text-center mb-8">
            <div className="inline-flex mb-4">
              <img src={logo} alt={APP_NAME} className="h-20 w-auto" />
            </div>
           
            <h1 className="text-2xl font-bold text-gray-900">Forgot Password?</h1>
            <p className="text-gray-600 mt-2">
              No worries! Enter your email and we'll send you reset instructions.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <Input
              label="Email Address"
              type="email"
              placeholder="admin@example.com"
              error={errors.email?.message as string}
              {...register('email')}
              autoFocus
            />

            <Button
              type="submit"
              variant="primary"
              className="w-full"
              loading={loading}
            >
              Send Reset Link
            </Button>

            <div className="text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Login
              </Link>
            </div>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
              <p className="font-medium text-gray-900 mb-2">🔒 Security Note</p>
              <p>
                For security reasons, we'll send the reset link only if an active account 
                exists with this email address. 
              </p>
            </div>
          </div>
        </Card.Content>
      </Card>
    </div>
  );
};

export default ForgotPassword;
